import { apiGet, apiPost } from './apiClient.js';

/**
 * Ops Service
 * For system operations, health checks, and safeguards
 */

/**
 * Get global kill switch status
 */
export async function getKillSwitchStatus() {
  return apiGet('/admin/integrations/global/status');
}

/**
 * Enable global kill switch
 * @param {string} reason - Reason for enabling
 * @param {number} intentAuditEventId - Intent audit event ID
 */
export async function enableKillSwitch(reason, intentAuditEventId) {
  return apiPost('/admin/integrations/global/enable', {
    reason: reason.trim(),
    intentAuditEventId,
  });
}

/**
 * Disable global kill switch
 * @param {string} reason - Reason for disabling
 * @param {number} intentAuditEventId - Intent audit event ID
 */
export async function disableKillSwitch(reason, intentAuditEventId) {
  return apiPost('/admin/integrations/global/disable', {
    reason: reason.trim(),
    intentAuditEventId,
  });
}

/**
 * Get system health
 */
export async function getSystemHealth() {
  return apiGet('/api/health');
}

/**
 * Get system readiness
 */
export async function getSystemReadiness() {
  return apiGet('/api/readyz');
}

/**
 * Get admin me (capabilities + actorId)
 */
export async function getAdminMe() {
  return apiGet('/admin/me');
}

/**
 * Get recent critical events
 * @param {number} limit - Max number of events (default 20)
 */
export async function getRecentCriticalEvents(limit = 20) {
  const criticalActions = [
    'integration.sync.completed',
    'integration.sync.requested',
    'admin.api_key.created',
    'admin.api_key.revoked',
    'admin.integrations.global.disabled',
    'admin.integrations.global.enabled',
    'rbac.role.assigned',
    'rbac.role.removed',
  ];

  // Query for events with outcome=fail OR action in critical list
  // We'll need to make multiple queries or use a search query
  // For now, query with outcome=fail and limit, then filter client-side
  const response = await apiGet(`/admin/audit-events?limit=${limit * 2}&outcome=failure`);
  
  // Also query for critical actions
  const criticalPromises = criticalActions.map(action =>
    apiGet(`/admin/audit-events?limit=5&action=${action}`).catch(() => ({ items: [] }))
  );
  const criticalResponses = await Promise.all(criticalPromises);
  
  // Combine and deduplicate
  const allEvents = [];
  if (response.items) {
    allEvents.push(...response.items);
  }
  criticalResponses.forEach(res => {
    if (res.items) {
      allEvents.push(...res.items);
    }
  });

  // Deduplicate by id and sort by timestamp
  const seen = new Set();
  const unique = allEvents.filter(event => {
    if (seen.has(event.id)) return false;
    seen.add(event.id);
    return true;
  });

  // Sort by timestamp descending and take limit
  unique.sort((a, b) => new Date(b.ts) - new Date(a.ts));
  return { items: unique.slice(0, limit) };
}

